import log from '../lib/log.js';
import relay from './jm-host-relay.js';
import {whenCollabSettled} from './jm-collab.js';
import {setReadOnly} from './jm-read-only.js';

const SAVE_TIMEOUT_MS = 30000;
const SAVE_SETTLE_MAX_MS = 5000;

const base64ToUint8Array = base64 => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

const uint8ArrayToBase64 = bytes => {
    const CHUNK_SIZE = 0x8000;
    let result = '';
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
        result += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK_SIZE));
    }
    return btoa(result);
};

const assetTypeFor = (storage, dataFormat) => {
    if (dataFormat === 'svg') return storage.AssetType.ImageVector;
    if (dataFormat === 'wav' || dataFormat === 'mp3') return storage.AssetType.Sound;
    return storage.AssetType.ImageBitmap;
};

const cacheAssets = (vm, assets) => {
    if (!assets || assets.length === 0) return;
    const storage = vm.runtime.storage;
    for (const asset of assets) {
        try {
            storage.builtinHelper._store(
                assetTypeFor(storage, asset.dataFormat),
                storage.DataFormat[asset.dataFormat.toUpperCase()] || asset.dataFormat,
                base64ToUint8Array(asset.data),
                asset.assetId
            );
        } catch (error) {
            log.error(`JuiceMind host bridge: could not cache asset ${asset.assetId}`, error);
        }
    }
};

const collectAssets = vm => {
    const assets = [];
    const seen = new Set();
    for (const target of vm.runtime.targets) {
        for (const costume of target.sprite.costumes) {
            if (costume.asset && !seen.has(costume.assetId)) {
                seen.add(costume.assetId);
                assets.push({
                    assetId: costume.assetId,
                    dataFormat: costume.dataFormat,
                    data: uint8ArrayToBase64(new Uint8Array(costume.asset.data))
                });
            }
        }
        for (const sound of target.sprite.sounds) {
            if (sound.asset && !seen.has(sound.assetId)) {
                seen.add(sound.assetId);
                assets.push({
                    assetId: sound.assetId,
                    dataFormat: sound.dataFormat,
                    data: uint8ArrayToBase64(new Uint8Array(sound.asset.data))
                });
            }
        }
    }
    return assets;
};

export const saveProject = (projectId, vmState) => new Promise((resolve, reject) => {
    const onResponse = event => {
        if (!relay.accept(event) || !event.data) return;
        if (event.data.type === 'SCRATCH_SAVE_COMPLETE') {
            window.removeEventListener('message', onResponse);
            window._projectSaved = true;
            resolve({id: event.data.projectId || projectId || 'local'});
        } else if (event.data.type === 'SCRATCH_SAVE_ERROR') {
            window.removeEventListener('message', onResponse);
            reject(new Error(event.data.error || 'Save failed'));
        }
    };
    window.addEventListener('message', onResponse);
    relay.post({
        type: 'SCRATCH_SAVE_PROJECT',
        projectId,
        projectJson: vmState,
        assets: collectAssets(window.vm)
    });
    setTimeout(() => {
        window.removeEventListener('message', onResponse);
        reject(new Error('Save timed out'));
    }, SAVE_TIMEOUT_MS);
});

const whenBootProjectLoaded = vm => {
    if (vm.runtime.getTargetForStage()) return Promise.resolve();
    return new Promise(resolve => vm.runtime.once('PROJECT_LOADED', resolve));
};

let installed = false;

const installHostBridge = vm => {
    if (installed) return;
    installed = true;

    let loadChain = Promise.resolve();

    const runLoad = async (projectData, assets) => {
        try {
            await whenBootProjectLoaded(vm);
            cacheAssets(vm, assets);
            await vm.loadProject(projectData);
            relay.post({type: 'SCRATCH_PROJECT_LOADED'});
        } catch (error) {
            log.error('JuiceMind host bridge: project load failed', error);
            relay.post({type: 'SCRATCH_PROJECT_LOADED', error: error && error.message});
        }
    };

    window.addEventListener('message', event => {
        if (!relay.accept(event) || !event.data || !event.data.type) return;
        switch (event.data.type) {
        case 'SCRATCH_LOAD_PROJECT':
            if (event.data.projectData) {
                loadChain = loadChain.then(() => runLoad(event.data.projectData, event.data.assets));
            }
            break;
        case 'SCRATCH_SAVE_STATE':
            window.__JM_SAVE__ = event.data.state;
            window.__JM_DIRTY__ = event.data.state !== 'saved';
            window.dispatchEvent(new CustomEvent('jm:save'));
            break;
        case 'SCRATCH_SET_HISTORY_ENABLED':
            window.__JM_HISTORY__ = !!event.data.enabled;
            window.dispatchEvent(new CustomEvent('jm:history'));
            break;
        case 'SCRATCH_SET_READ_ONLY':
            setReadOnly(!!event.data.readOnly);
            break;
        case 'SCRATCH_REQUEST_SAVE': {
            const projectId = event.data.projectId || null;
            whenCollabSettled(SAVE_SETTLE_MAX_MS)
                .then(() => saveProject(projectId, vm.toJSON()).catch(() => {}));
            break;
        }
        case 'SCRATCH_REQUEST_PROJECT':
            relay.post({
                type: 'SCRATCH_PROJECT_DATA',
                projectId: event.data.projectId || 'local',
                projectJson: vm.toJSON(),
                assets: collectAssets(vm)
            });
            break;
        }
    });

    vm.on('PROJECT_CHANGED', () => {
        window.__JM_DIRTY__ = true;
        relay.post({type: 'SCRATCH_PROJECT_CHANGED'});
    });

    relay.post({type: 'SCRATCH_VM_READY'});
};

export default installHostBridge;
